import { COMMENDA_COMPANY_IDS, COMMENDA_IO_EMAIL_DOMAIN } from './constants';

interface WithEmail {
  email: string;
}

/**
 * Filters out members with @commenda.io emails in production environment,
 * unless the company is a Commenda company.
 */
export function filterCommendaIoEmails<T extends WithEmail>(
  members: T[] | undefined,
  companyId: number
): T[] {
  if (!members) {
    return [];
  }

  // Only filter on production environment (not staging/demo/development)
  if (process.env.NEXT_PUBLIC_CLIENT_ENV !== 'production') {
    return members;
  }

  if (COMMENDA_COMPANY_IDS.includes(companyId)) {
    return members;
  }

  return members.filter(
    (member) => !member.email.includes(COMMENDA_IO_EMAIL_DOMAIN)
  );
}

/**
 * A seeded demo account's synthetic addresses carry a numeric `+demo<id>` tag, so one roster of members can be
 * seeded for several demo accounts without colliding on the unique email column. The tag is an implementation
 * detail of the seed, not part of the address the firm is meant to show.
 *
 * Digits and an immediately following `@` are both required, which leaves a genuine `+demo…` sub-address and the
 * lettered tag on a self-serve demo signup untouched.
 */
const DEMO_SEED_EMAIL_TAG = /\+demo\d+(?=@)/;

export function getDisplayEmail(email: string): string {
  return email.replace(DEMO_SEED_EMAIL_TAG, '');
}

export function isCommendaInternalEmail(
  email: string | null | undefined
): boolean {
  return email?.toLowerCase().endsWith(COMMENDA_IO_EMAIL_DOMAIN) ?? false;
}
